import ApiClient from '@core/api/client'
import MaintenancePage from '@core/components/MaintenancePage'
import Clarity from '@core/components/vendor/Clarity'
import { ClarityIdentify } from '@core/components/vendor/ClarityIdentify'
import config, { isTrue } from '@core/config'
import { ApiProvider } from '@core/hooks/useApi'
import { useMarketSync } from '@core/hooks/useMarketSync'
import { getMarketFromTimezone, isMaintenanceTimeInEST, uniqueId } from '@core/utils'
import { Provider as RollbarProvider } from '@rollbar/react'
import { useMemo, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { Toaster } from 'react-hot-toast'
import { RouterProvider } from 'react-router-dom'
import { useSearchParam } from 'react-use'
import './index.css'
import { createAppRouter } from './routes'

declare global {
  interface Window {
    clarity: (command: string, ...args: unknown[]) => void
    opera: string
    webkit?: {
      messageHandlers?: {
        webViewSetupBackButtonInterface?: {
          postMessage: (message: string) => void
        }
        webViewPreventBackInterface?: {
          postMessage: (message: string) => void
        }
        webViewProvideCloseButtonInterface?: {
          postMessage: (message: string) => void
        }
      }
    }
    AndroidPreventBackInterface?: {
      enable: () => void
    }
    AndroidProvideCloseButtonInterface?: {
      enable: (dialogTitle: string | null, dialogMessage: string | null) => void
      disable: () => void
    }
    AndroidShareInterface?: {
      share: (title: string, text: string) => void
    }
  }
}

const api = new ApiClient({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  useFaPhotoProxy: import.meta.env.VITE_USE_FA_PHOTO_PROXY === 'true',
})

const rollbarConfig = {
  accessToken: import.meta.env.VITE_ROLLBAR_ACCESS_TOKEN,
  environment: import.meta.env.VITE_ENVIRONMENT,
}

const {
  familyAlbumAutoLoginUrl,
  familyAlbumDownloadDeepLink,
  familyAlbumAutologinDeeplink,
  supportEmail,
  familyAlbumStoreDeepLink,
  clarityProjectId,
  isPrintifyPreviewLogsActive,
  explanationVideoUrl,
} = config

function AppContent({ api }: { api: ApiClient }) {
  useMarketSync(api, window.location.pathname)
  const router = createAppRouter({
    api,
    supportEmail,
    isPrintifyPreviewLogsActive,
    familyAlbumAutologinDeeplink,
    familyAlbumDownloadDeepLink,
    familyAlbumStoreDeepLink,
    familyAlbumAutoLoginUrl,
    explanationVideoUrl,
  })

  return <RouterProvider router={router} />
}

function Root() {
  const market = getMarketFromTimezone()
  const isPremiumUser = isTrue(useSearchParam('isPremiumUser') ?? 'false')
  const uuid = useSearchParam('uuid')
  const data = useSearchParam('data')
  const [sid] = useState(() => uniqueId())
  const session = useMemo(
    () => ({
      market,
      isPremiumUser,
      uuid,
      data,
      sid,
    }),
    [market, isPremiumUser, uuid, data, sid]
  )

  return (
    <RollbarProvider config={rollbarConfig}>
      <ClarityIdentify clarityProjectId={clarityProjectId} uuid={uuid} sid={sid} />
      {clarityProjectId && <Clarity projectId={clarityProjectId} />}
      <Toaster position="top-center" />
      <ApiProvider value={{ api, session }}>
        <AppContent api={api} />
      </ApiProvider>
    </RollbarProvider>
  )
}

if (isMaintenanceTimeInEST()) {
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(<MaintenancePage />)
} else {
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(<Root />)
}
